import { useState, useEffect } from "react";
import { useContent } from "contexts/contentContext/contentContext.js";
import Icon from "components/Elements/Icon/Icon.tsx";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Button from "components/Elements/Button/Button";
import RecipientItem from "./components/RecipientItem";
import { useToasts } from "react-toast-notifications";
import validateRecipient from "components/Pages/CreateJumboForm/Helpers/validateRecipient";

const MAX_RECIPIENTS = 50;

const generateId = () => {
  return `recipient_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const FormBuilderRecipientElement = ({
  tempField,
  setTempField,
  setfieldChanged,
  setUpdateToggle,
  recipientValidationErrors,
  setRecipientValidationErrors,
}) => {
  const { addToast } = useToasts();
  const { getTextByKey } = useContent();
  const [activeRecipientId, setActiveRecipientId] = useState(null);

  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    // Initialize recipients array if it doesn't exist
    if (!tempField.fields.recipients) {
      setTempField((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          recipients: [],
        },
      }));
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".recipient-item")) {
        setActiveRecipientId(null);
        setRecipientValidationErrors({});
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  

  const validateAllRecipients = () => {
    const recipients = tempField.fields.recipients || [];
    const allErrors = {};
    let hasError = false;
    recipients.forEach((recipient) => {
      const errors = validateRecipient(recipient);
      if (Object.keys(errors).length > 0) {
        allErrors[recipient.id] = errors;
        hasError = true;
      }
    });
    setRecipientValidationErrors(allErrors);
    return !hasError;
  };

  const handleAddRecipient = () => {
    // Validate all existing
    if (!validateAllRecipients()) {
      // Focus on the first invalid recipient
      const recipients = tempField.fields.recipients || [];
      for (const r of recipients) {
        const errors = validateRecipient(r);
        if (Object.keys(errors).length > 0) {
          setActiveRecipientId(r.id);
          break;
        }
      }
      return;
    }

    const nextRecipientsCount = (tempField.fields.recipients?.length || 0) + 1;
    if (nextRecipientsCount > MAX_RECIPIENTS) {
      setIsDisabled(true);
      addToast(getTextByKey("max-recipients-reached"), {
        appearance: "warning",
      });
      return;
    }

    // Add a new empty recipient
    setTempField((prev) => ({
      ...prev,
      fields: {
        ...prev.fields,
        recipients: [
          ...(prev.fields.recipients || []),
          { id: generateId(), name: "", email: "" },
        ],
      },
    }));
    setfieldChanged(true);
    setUpdateToggle(true);
    setRecipientValidationErrors({});
    setActiveRecipientId(null);
    if (nextRecipientsCount === MAX_RECIPIENTS) {
      setIsDisabled(true);
      addToast(getTextByKey("max-recipients-reached"), {
        appearance: "warning",
      });
    }
  };

  const handleDeleteRecipient = (recipientId) => {
    const newRecipients = tempField.fields.recipients.filter(
      (r) => r.id !== recipientId
    );
    setTempField({
      ...tempField,
      fields: {
        ...tempField.fields,
        recipients: newRecipients,
      },
    });
    setRecipientValidationErrors({});
    setActiveRecipientId(null);
    if (newRecipients.length < MAX_RECIPIENTS) {
      setIsDisabled(false);
    }
    setfieldChanged(true);
    setUpdateToggle(true);
  };

  useEffect(() => {
    const recipientsCount = tempField.fields.recipients?.length || 0;
    if (recipientsCount < MAX_RECIPIENTS) {
      setIsDisabled(false);
    }
  }, [tempField.fields.recipients]);

  const hasReachedMaxRecipients =
    (tempField.fields.recipients?.length || 0) >= MAX_RECIPIENTS;

  const moveRecipient = (dragIndex, hoverIndex) => {
    const recipients = [...tempField.fields.recipients];
    const draggedRecipient = recipients[dragIndex];
    recipients.splice(dragIndex, 1);
    recipients.splice(hoverIndex, 0, draggedRecipient);
    setTempField({
      ...tempField,
      fields: {
        ...tempField.fields,
        recipients: recipients,
      },
    });
    setfieldChanged(true);
    setUpdateToggle(true);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="recipients-container input-part">
        <p className="recipients-list-title">
          {getTextByKey("recipients-list-title")}{" "}
          {tempField.fields.recipients?.length
            ? `(${tempField.fields.recipients.length})`
            : ""}
        </p>
        <ul className="recipients-list">
          {tempField.fields.recipients?.map((recipient, index) => (
            <RecipientItem
              key={recipient.id}
              recipient={recipient}
              index={index}
              moveRecipient={moveRecipient}
              activeRecipientId={activeRecipientId}
              setActiveRecipientId={setActiveRecipientId}
              tempField={tempField}
              setTempField={setTempField}
              setfieldChanged={setfieldChanged}
              setUpdateToggle={setUpdateToggle}
              validationErrors={recipientValidationErrors[recipient.id] || {}}
              setValidationErrors={setRecipientValidationErrors}
              handleDeleteRecipient={handleDeleteRecipient}
            />
          ))}
        </ul>
        <Button
          className="add-recipient-button"
          onClick={handleAddRecipient}
          disabled={hasReachedMaxRecipients || isDisabled}
        >
          <Icon name="plus" />
          {getTextByKey("add-recipient")}
        </Button>
      </div>
    </DndProvider>
  );
};

export default FormBuilderRecipientElement;
